/**
 * ContentProtection — mount once at the app root or on protected pages.
 *
 * Implements 4 defensive layers:
 *   1. Attribution auto-appended on any text copy (Ctrl/Cmd+C, right-click copy).
 *   2. Right-click blocked on branded infographics (elements flagged with
 *      class "kl-protected-image" or data-protected-image). Toast displayed.
 *   3. Print stylesheet injected globally — adds a diagonal watermark when
 *      the visitor prints or "Save as PDF"s the page.
 *   4. Invisible canary text seeded into all <p> and <li> elements on the
 *      page (zero-width Unicode) — allows leak-detection via Google search.
 *
 * Zero visible impact on real users. Only friction/detection for copiers.
 */
import { useEffect } from "react";

const CANARY = "\u200B\u200C\u200D\u200B"; // 4-char zero-width fingerprint

export default function ContentProtection({
  attributionUrl,          // canonical page URL to append on copy
  attributionText = "© Karine Lauzon RN Clinics",
  minCopyLengthForAttribution = 30,
  enableCanary = true,
  enablePrintWatermark = true,
  enableImageProtection = true,
  enableCopyAttribution = true,
}) {
  // ─── 1. Copy attribution ──────────────────────────────────────
  useEffect(() => {
    if (!enableCopyAttribution) return;
    const handler = (e) => {
      const selection = document.getSelection()?.toString() || "";
      if (selection.length < minCopyLengthForAttribution) return;
      const url = attributionUrl || window.location.href;
      const suffix = `\n\n— ${attributionText} · Source: ${url}`;
      // Do not double-append if the selection already contains the attribution
      if (selection.includes(attributionText)) return;
      try {
        e.clipboardData.setData("text/plain", `${selection}${suffix}`);
        // Include a plain-HTML version so rich-text paste destinations
        // (Google Docs, Word) get a subtle italic footer as well.
        const html = `${selection.replace(/\n/g, "<br>")}<br><br><em style="color:#999;font-size:0.85em">— ${attributionText} · Source: <a href="${url}">${url}</a></em>`;
        e.clipboardData.setData("text/html", html);
        e.preventDefault();
      } catch (_) { /* silently ignore if clipboardData is unavailable */ }
    };
    document.addEventListener("copy", handler);
    return () => document.removeEventListener("copy", handler);
  }, [attributionUrl, attributionText, minCopyLengthForAttribution, enableCopyAttribution]);

  // ─── 2. Right-click blocked on branded infographics ───────────
  useEffect(() => {
    if (!enableImageProtection) return;
    const showToast = () => {
      // Reuse existing toast if present, else create one
      let toast = document.getElementById("kl-protected-image-toast");
      if (!toast) {
        toast = document.createElement("div");
        toast.id = "kl-protected-image-toast";
        toast.style.cssText = `
          position: fixed; top: 100px; left: 50%; transform: translateX(-50%);
          background: rgba(0,0,0,0.85); color: #fff; padding: 12px 20px;
          border-radius: 12px; font-size: 13px; font-family: 'Inter', sans-serif;
          z-index: 9999; box-shadow: 0 10px 40px rgba(0,0,0,0.4);
          border: 1px solid rgba(255,255,255,0.15); pointer-events: none;
          opacity: 0; transition: opacity 200ms ease;
        `;
        toast.textContent = "© Karine Lauzon RN Clinics — image protected.";
        document.body.appendChild(toast);
      }
      toast.style.opacity = "1";
      clearTimeout(toast._klHideTimer);
      toast._klHideTimer = setTimeout(() => { toast.style.opacity = "0"; }, 2200);
    };
    const isProtected = (el) => {
      if (!el || el.nodeType !== 1) return false;
      return el.matches?.("img.kl-protected-image, img[data-protected-image], [data-protected-image] img");
    };
    const contextHandler = (e) => {
      if (isProtected(e.target)) {
        e.preventDefault();
        showToast();
      }
    };
    const dragHandler = (e) => {
      if (isProtected(e.target)) e.preventDefault();
    };
    document.addEventListener("contextmenu", contextHandler);
    document.addEventListener("dragstart", dragHandler);
    return () => {
      document.removeEventListener("contextmenu", contextHandler);
      document.removeEventListener("dragstart", dragHandler);
    };
  }, [enableImageProtection]);

  // ─── 3. Global print stylesheet + watermark ───────────────────
  useEffect(() => {
    if (!enablePrintWatermark) return;
    const styleId = "kl-print-protection-style";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @media print {
        body::before {
          content: "© KARINE LAUZON RN CLINICS · UNAUTHORIZED COPY · karinelauzon.com";
          position: fixed;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-30deg);
          font-size: 42px;
          color: rgba(0,0,0,0.12);
          font-weight: 900;
          letter-spacing: 4px;
          white-space: nowrap;
          pointer-events: none;
          z-index: 99999;
        }
        body::after {
          content: "© Karine Lauzon RN Clinics · karinelauzon.com · Content is copyright-protected. Unauthorized reproduction prohibited.";
          position: fixed;
          bottom: 8px; left: 8px; right: 8px;
          font-size: 9px; color: #666;
          text-align: center;
          border-top: 1px solid #ddd;
          padding-top: 4px;
        }
      }
    `;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, [enablePrintWatermark]);

  // ─── 4. Canary seeding (invisible zero-width fingerprint) ─────
  useEffect(() => {
    if (!enableCanary) return;
    // Small delay so React finishes rendering the current page.
    const timer = setTimeout(() => {
      const targets = document.querySelectorAll("p, li, h2, h3");
      targets.forEach((el) => {
        if (el.dataset.klCanary === "1") return;
        if (el.closest("input, textarea, code, pre, [contenteditable]")) return;
        // Walk descendant text nodes; find the first substantial one.
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        let node = walker.nextNode();
        while (node) {
          if (node.nodeValue && node.nodeValue.trim().length > 40) {
            const idx = 20;
            node.nodeValue = node.nodeValue.slice(0, idx) + CANARY + node.nodeValue.slice(idx);
            el.dataset.klCanary = "1";
            break;
          }
          node = walker.nextNode();
        }
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [enableCanary]);

  return null;
}
